import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Rocket,
  Calendar,
  CheckCircle,
  Circle,
  TrendingUp,
  Share2,
  Download,
  Copy,
  Check,
  Clock,
  Users,
  MessageSquare,
  Target,
  Award,
  Zap,
} from 'lucide-react';

// Launch phase tracking
type LaunchPhase = 'pre-launch' | 'soft-launch' | 'public-launch' | 'post-launch';

interface LaunchTask {
  id: string;
  title: string;
  description: string;
  category: 'outreach' | 'testing' | 'content' | 'technical';
  completed: boolean;
  priority: 'high' | 'medium' | 'low';
}

const mockLaunchTasks: LaunchTask[] = [
  {
    id: 'task-1',
    title: 'Share demo with 10 neighbors',
    description: 'Show voting demo to at least 10 neighbors and collect feedback',
    category: 'outreach',
    completed: true,
    priority: 'high',
  },
  {
    id: 'task-2',
    title: 'Collect 5 feedback responses',
    description: 'Submit at least 5 neighbor feedback items through the dashboard',
    category: 'outreach',
    completed: true,
    priority: 'high',
  },
  {
    id: 'task-3',
    title: 'Test all 4 feature demos',
    description: 'Try voting, proposals, group buying, and discussions demos',
    category: 'testing',
    completed: false,
    priority: 'high',
  },
  {
    id: 'task-4',
    title: 'Vote on feature priorities',
    description: 'Rank upcoming features by importance',
    category: 'testing',
    completed: false,
    priority: 'medium',
  },
  {
    id: 'task-5',
    title: 'Post on Facebook/Nextdoor',
    description: 'Share platform intro using provided social media templates',
    category: 'content',
    completed: false,
    priority: 'high',
  },
  {
    id: 'task-6',
    title: 'Create 3 invite links',
    description: 'Generate personalized invite links for neighbors',
    category: 'content',
    completed: false,
    priority: 'medium',
  },
  {
    id: 'task-7',
    title: 'Update your Champion profile',
    description: 'Add photo and bio to Champions Directory',
    category: 'technical',
    completed: false,
    priority: 'low',
  },
  {
    id: 'task-8',
    title: 'Report any bugs/issues',
    description: 'Send message if you encounter problems',
    category: 'technical',
    completed: false,
    priority: 'medium',
  },
];

const socialTemplates = [
  {
    id: 'facebook-intro',
    platform: 'Facebook',
    title: 'Platform Introduction',
    content: `🏘️ Exciting news for Lake Charles neighbors!

I'm testing a new community platform that could change how we make decisions together. Instead of endless Facebook arguments, we'd have real votes, organized proposals, and group discounts.

I'm one of 10 Champions helping test this before it launches. Want to learn more?

👉 [YOUR INVITE LINK]

What features would YOU want in a neighborhood app?`,
  },
  {
    id: 'nextdoor-intro',
    platform: 'Nextdoor',
    title: 'Neighborhood App Test',
    content: `Hey neighbors! 👋

I've been selected as a Champion to test a new community platform for Lake Charles. Think: real voting on neighborhood issues, resident proposals, and group purchasing power.

Still in testing phase, but looking promising. Free trial coming soon!

Interested? Drop a comment and I'll send details.

#LakeCharles #CommunityTools`,
  },
  {
    id: 'direct-invite',
    platform: 'Direct Message',
    title: 'Personal Invite',
    content: `Hi [NAME]!

I wanted to reach out personally - I'm testing a new community platform for Lake Charles neighborhoods. It has features like:

✅ Real voting (not Facebook polls)
✅ Resident proposals with feedback
✅ Group buying for discounts
✅ Organized discussions

Free trial launching in [X] days. Want early access?

[YOUR INVITE LINK]

Let me know what you think!`,
  },
];

const launchMilestones = [
  { date: '2025-11-15', label: 'Free Trial Launch', phase: 'soft-launch' as LaunchPhase },
  { date: '2025-12-01', label: 'Public Launch', phase: 'public-launch' as LaunchPhase },
  { date: '2026-01-15', label: '500 Users Goal', phase: 'post-launch' as LaunchPhase },
];

export function LaunchPreparation() {
  const [copiedTemplate, setCopiedTemplate] = useState<string | null>(null);
  const [inviteLink] = useState('https://lakecharles.toolboxlogic.com/invite/SARAH-CHAMPION-3');

  // Calculate current launch phase and countdown
  const today = new Date();
  const launchDate = new Date('2025-11-15');
  const daysUntilLaunch = Math.ceil(
    (launchDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  const currentPhase: LaunchPhase = daysUntilLaunch > 0 ? 'pre-launch' : 'soft-launch';

  // Calculate task progress
  const completedTasks = mockLaunchTasks.filter((t) => t.completed).length;
  const totalTasks = mockLaunchTasks.length;
  const progressPercentage = Math.round((completedTasks / totalTasks) * 100);

  // Group tasks by category
  const tasksByCategory = {
    outreach: mockLaunchTasks.filter((t) => t.category === 'outreach'),
    testing: mockLaunchTasks.filter((t) => t.category === 'testing'),
    content: mockLaunchTasks.filter((t) => t.category === 'content'),
    technical: mockLaunchTasks.filter((t) => t.category === 'technical'),
  };

  // Handle copy template
  const handleCopyTemplate = async (id: string, content: string) => {
    try {
      const contentWithLink = content.replace('[YOUR INVITE LINK]', inviteLink);
      await navigator.clipboard.writeText(contentWithLink);
      setCopiedTemplate(id);
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle copy invite link
  const handleCopyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopiedTemplate('invite-link');
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const phaseColors = {
    'pre-launch': 'bg-blue-100 text-blue-800 border-blue-200',
    'soft-launch': 'bg-orange-100 text-orange-800 border-orange-200',
    'public-launch': 'bg-green-100 text-green-800 border-green-200',
    'post-launch': 'bg-purple-100 text-purple-800 border-purple-200',
  };

  const phaseLabels = {
    'pre-launch': 'Pre-Launch Testing',
    'soft-launch': 'Soft Launch (Free Trial)',
    'public-launch': 'Public Launch',
    'post-launch': 'Growth Phase',
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Launch Preparation</h1>
        <p className="text-muted-foreground">
          Get ready for launch! Track your progress, share with neighbors, and prepare for the big
          day.
        </p>
      </div>

      {/* Launch Countdown */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white rounded-lg">
                <Rocket className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {daysUntilLaunch > 0 ? `${daysUntilLaunch} Days Until Launch` : 'Launch Active!'}
                </h2>
                <p className="text-muted-foreground">Free trial begins November 15, 2025</p>
                <Badge className={`mt-2 ${phaseColors[currentPhase]}`}>
                  {phaseLabels[currentPhase]}
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Your Progress</p>
              <p className="text-3xl font-bold text-primary">{progressPercentage}%</p>
              <p className="text-sm text-muted-foreground">
                {completedTasks} of {totalTasks} tasks
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Launch Tasks Checklist */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Launch Tasks</CardTitle>
              <CardDescription>
                Complete these tasks to prepare for launch and maximize your impact
              </CardDescription>
            </div>
            <Progress value={progressPercentage} className="w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Outreach Tasks */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Neighbor Outreach</h3>
                <Badge variant="outline">
                  {tasksByCategory.outreach.filter((t) => t.completed).length}/
                  {tasksByCategory.outreach.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {tasksByCategory.outreach.map((task) => (
                  <Card
                    key={task.id}
                    className={task.completed ? 'bg-green-50 border-green-200' : ''}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {task.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.completed ? 'text-green-800' : ''}`}>
                            {task.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        </div>
                        {task.priority === 'high' && !task.completed && (
                          <Badge variant="destructive" className="text-xs">
                            High Priority
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Testing Tasks */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Feature Testing</h3>
                <Badge variant="outline">
                  {tasksByCategory.testing.filter((t) => t.completed).length}/
                  {tasksByCategory.testing.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {tasksByCategory.testing.map((task) => (
                  <Card
                    key={task.id}
                    className={task.completed ? 'bg-green-50 border-green-200' : ''}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {task.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.completed ? 'text-green-800' : ''}`}>
                            {task.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Content Tasks */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Share2 className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Content Creation</h3>
                <Badge variant="outline">
                  {tasksByCategory.content.filter((t) => t.completed).length}/
                  {tasksByCategory.content.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {tasksByCategory.content.map((task) => (
                  <Card
                    key={task.id}
                    className={task.completed ? 'bg-green-50 border-green-200' : ''}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {task.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.completed ? 'text-green-800' : ''}`}>
                            {task.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        </div>
                        {task.priority === 'high' && !task.completed && (
                          <Badge variant="destructive" className="text-xs">
                            High Priority
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Technical Tasks */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Technical Setup</h3>
                <Badge variant="outline">
                  {tasksByCategory.technical.filter((t) => t.completed).length}/
                  {tasksByCategory.technical.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {tasksByCategory.technical.map((task) => (
                  <Card
                    key={task.id}
                    className={task.completed ? 'bg-green-50 border-green-200' : ''}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {task.completed ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground mt-0.5" />
                        )}
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.completed ? 'text-green-800' : ''}`}>
                            {task.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Your Invite Link */}
      <Card>
        <CardHeader>
          <CardTitle>Your Personal Invite Link</CardTitle>
          <CardDescription>
            Share this unique link with neighbors. We'll track signups from your invites!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input value={inviteLink} readOnly className="font-mono text-sm" />
            <Button variant="outline" onClick={handleCopyInviteLink}>
              {copiedTemplate === 'invite-link' ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Social Media Toolkit */}
      <Card>
        <CardHeader>
          <CardTitle>Social Media Toolkit</CardTitle>
          <CardDescription>
            Pre-written posts ready to share on Facebook, Nextdoor, or via direct message
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {socialTemplates.map((template) => {
              const isCopied = copiedTemplate === template.id;

              return (
                <Card key={template.id} className="bg-muted/30">
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge variant="outline" className="mb-2">
                            {template.platform}
                          </Badge>
                          <h4 className="font-medium">{template.title}</h4>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleCopyTemplate(template.id, template.content)}
                        >
                          {isCopied ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Post
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Template Preview */}
                      <div className="bg-white rounded-lg p-4 border text-sm whitespace-pre-line">
                        {template.content}
                      </div>

                      <p className="text-xs text-muted-foreground">
                        💡 Your invite link will be automatically inserted when you copy
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Launch Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Launch Timeline</CardTitle>
          <CardDescription>Key milestones on the road to public launch</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {launchMilestones.map((milestone, index) => {
              const milestoneDate = new Date(milestone.date);
              const isPast = milestoneDate < today;
              const daysAway = Math.ceil(
                (milestoneDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
              );

              return (
                <div key={index} className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isPast ? 'bg-green-100' : 'bg-primary/10'}`}>
                    {isPast ? (
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    ) : (
                      <Calendar className="h-6 w-6 text-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{milestone.label}</h4>
                      <Badge className={phaseColors[milestone.phase]}>
                        {phaseLabels[milestone.phase]}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {milestoneDate.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {!isPast && daysAway >= 0 && ` • ${daysAway} days away`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Impact Summary */}
      <Alert>
        <Award className="h-4 w-4" />
        <AlertDescription>
          <strong>You're making a difference!</strong> Your feedback and outreach are shaping the
          future of Lake Charles community engagement. Keep up the great work!
        </AlertDescription>
      </Alert>
    </div>
  );
}
